import type { StoreDayOpeningTimeFragment } from '@shared/gql/document-nodes'
import type { FC } from 'react'

import { toFormattedDate, toFormattedWeekday } from 'domain/date-time'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { OpeningTime } from './OpeningTime'
import { useOpeningTime } from './StoreOpeningTimes'

const WeekInfo = styled.div(({ theme }) => ({
  ...theme.variants.body2.regular,

  ['> * + *']: {
    marginTop: theme.spacings.xSmall,
  },
}))

const OpeningTimesList = styled.ul(({ theme }) => ({
  ['> * + *']: {
    marginTop: theme.spacings.xxSmall,
  },
}))

const OpeningTimesItem = styled.li(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  columnGap: theme.spacings.xxSmall,

  ['> *:first-child']: {
    flex: '0 1 64px',
  },
}))

const WeekdayLabel = styled.span(({ theme }) => ({
  fontWeight: theme.fontWeights.medium,
}))

const StatusMessage = styled.p(({ theme }) => theme.variants.caption.regular)

const formatWeekDay = (date: string) => toFormattedWeekday(date, { format: 'short' })

const getWeekDayLabel = (openingTimes: StoreDayOpeningTimeFragment[]) => {
  const first = formatWeekDay(openingTimes[0].date)

  if (openingTimes.length > 1) {
    const last = formatWeekDay(openingTimes[openingTimes.length - 1].date)

    return `${first}—${last}`
  }

  return first
}

export const WeekOpeningTimes: FC<{ weekOpeningTimes: StoreDayOpeningTimeFragment[] }> = ({
  weekOpeningTimes,
}) => {
  const getOpeningTime = useOpeningTime()
  const { t } = useTranslation()

  const startDate = toFormattedDate(weekOpeningTimes[0].date, { weekday: false })
  const endDate = toFormattedDate(weekOpeningTimes[weekOpeningTimes.length - 1].date, {
    weekday: false,
  })

  const weekdays = weekOpeningTimes.slice(0, 5)
  const weekend = weekOpeningTimes.slice(5, 7)

  const isWeekdaysGroupable =
    weekdays.filter((openingTimes) => {
      return (
        weekdays[0].exceptional === openingTimes.exceptional &&
        weekdays[0].message?.fi === openingTimes.message?.fi &&
        getOpeningTime(weekdays[0]) === getOpeningTime(openingTimes)
      )
    }).length === 5

  const week = isWeekdaysGroupable ? [weekdays, ...weekend] : [...weekdays, ...weekend]

  const getOpeningTimeMessage = (openingTime: StoreDayOpeningTimeFragment) =>
    openingTime.exceptional ? openingTime.message?.fi || t('Exceptional hours for the day') : null

  return (
    <WeekInfo>
      <span data-test-id="weekdates">
        {startDate}—{endDate}
      </span>
      <OpeningTimesList data-test-id="week-opening-times">
        {week.map((openingTimeGroup, i) => {
          const isGroup = Array.isArray(openingTimeGroup)
          const openingTime = isGroup ? openingTimeGroup[0] : openingTimeGroup
          const openingTimeMessage = getOpeningTimeMessage(openingTime)

          return (
            <OpeningTimesItem key={i} data-test-id="weekday-opening-times">
              <WeekdayLabel data-test-id="weekday-label">
                {getWeekDayLabel(isGroup ? openingTimeGroup : [openingTimeGroup])}:
              </WeekdayLabel>
              <div>
                <OpeningTime isExceptional={openingTime.exceptional ?? false}>
                  {getOpeningTime(openingTime)}
                </OpeningTime>
                {openingTimeMessage && <StatusMessage>{openingTimeMessage}</StatusMessage>}
              </div>
            </OpeningTimesItem>
          )
        })}
      </OpeningTimesList>
    </WeekInfo>
  )
}
